import { keepPreviousData } from "@tanstack/query-core";
import {
  RefCallback,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { rqClient } from "@/shared/api/instance";
import type { ApiSchemas } from "@/shared/api/schema";
import { isMacPlatform } from "@/shared/lib/platform";
import { useDebounce } from "@/shared/lib/use-debounce";

import { getSearchType, getResultRoute } from "./domain/routing";
import type { Tab, SearchResult } from "./domain/types";

type SearchResultsPage = ApiSchemas["SearchResultsResponse"];

// Inlined navigation helpers (formerly search-navigation.ts)
function calculateNextSelectedIndex(
  currentIndex: number,
  direction: "up" | "down",
  resultsLength: number,
): number {
  if (direction === "down") {
    return currentIndex < resultsLength - 1 ? currentIndex + 1 : currentIndex;
  }
  if (direction === "up") {
    return currentIndex > 0 ? currentIndex - 1 : -1;
  }
  return currentIndex;
}

function isValidSelection(
  selectedIndex: number,
  resultsLength: number,
): boolean {
  return selectedIndex >= 0 && selectedIndex < resultsLength;
}

export function useSearch(
  open: boolean,
  onOpenChange: (open: boolean) => void,
  limit: number = 20,
) {
  const navigate = useNavigate();
  const isMac = isMacPlatform();

  // State
  const [activeTab, setActiveTab] = useState<Tab>("all");
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  // Derived state
  const debouncedQuery = useDebounce(query, 300);
  const searchType = getSearchType(activeTab);

  // React Query infinite query (from search-model.ts)
  const { fetchNextPage, data, isFetchingNextPage, isPending, hasNextPage } =
    rqClient.useInfiniteQuery(
      "get",
      "/api/v1/search",
      {
        params: {
          query: {
            page: 1,
            limit,
            query: debouncedQuery || "",
            search_type: searchType || undefined,
          },
        },
      },
      {
        enabled: !!debouncedQuery && debouncedQuery.trim().length > 0,
        initialPageParam: 1,
        pageParamName: "page",
        getNextPageParam: (
          lastPage: SearchResultsPage,
          _lastPageData: unknown,
          lastPageParams: unknown,
        ) => {
          if (!lastPage.pagination || lastPage.pagination.pages === undefined)
            return null;
          return Number(lastPageParams) < lastPage.pagination.pages
            ? Number(lastPageParams) + 1
            : null;
        },
        placeholderData: keepPreviousData,
      },
    );

  const results = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.results ?? []) ??
        []) as SearchResult[],
    [data?.pages],
  );

  // Intersection observer cursor (from search-model.ts)
  const cursorRef: RefCallback<HTMLDivElement> = useCallback(
    (el) => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.5 },
      );
      if (el) {
        observer.observe(el);
        return () => observer.disconnect();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  // Selection handler (from view-model)
  const handleSelect = useCallback(
    (result: SearchResult) => {
      onOpenChange(false);
      const route = getResultRoute(result);
      navigate(route.path);
    },
    [onOpenChange, navigate],
  );

  // Cmd+K global shortcut (from use-search-keyboard.ts)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  // Focus input when dialog opens (from view-model)
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
    if (!open) {
      setSelectedIndex(-1);
    }
  }, [open]);

  // Reset selected index when query or tab changes (from view-model)
  useEffect(() => {
    setSelectedIndex(-1);
  }, [debouncedQuery, activeTab]);

  // Body scroll lock (from view-model)
  useEffect(() => {
    if (open) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [open]);

  // Keyboard navigation for results (from view-model)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open || !debouncedQuery || !results || results.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          calculateNextSelectedIndex(prev, "down", results.length),
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          calculateNextSelectedIndex(prev, "up", results.length),
        );
      } else if (
        e.key === "Enter" &&
        isValidSelection(selectedIndex, results.length)
      ) {
        e.preventDefault();
        const selectedResult = results[selectedIndex];
        if (selectedResult) handleSelect(selectedResult);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, debouncedQuery, results, selectedIndex, handleSelect]);

  return {
    // Dialog state
    open,
    onOpenChange,

    // Navigation state
    selectedIndex,
    inputRef,
    isMac,

    // Query state
    activeTab,
    query,
    setQuery,
    setActiveTab,
    debouncedQuery,

    // Results
    results,
    isPending,
    isFetchingNextPage,
    cursorRef,

    // Actions
    handleSelect,
  };
}

export type UseSearchReturn = ReturnType<typeof useSearch>;
